import React from 'react';

import { useQuery } from '@betterment/js-runtime';
import { Box, Flex, SectionLayout, Text, theme } from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import BettermentForAdvisorsLogo from './BettermentForAdvisorsLogo';

const SecondaryLink = styled.a`
  color: ${theme.colors.bmtGrey50};
  text-decoration: underline;

  &:visited {
    color: ${theme.colors.bmtGrey50};
  }
`;

export interface FooterData {
  legalUrl: string;
  privacyPolicyUrl: string;
  trademarksUrl: string;
  disclosureUrl: string;
}

const Footer: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { data } = useQuery<FooterData>('/api/v1/footer_links');

  if (data) {
    return (
      <SectionLayout pt="12">
        <SectionLayout.ContentWrapper>
          <Flex flexDirection={['column', null, 'row']}>
            <Flex.Item width={[null, 1, 1 / 4]} px={4}>
              <Box mb="3">
                <BettermentForAdvisorsLogo css={{ width: '100px' }} />
              </Box>
              <Text fontSize="1" color="bmtGrey50" lineHeight="20px" as="p">
                This website is operated and maintained by Betterment LLC, an SEC Registered Investment Advisor.
                <Box as="span" display="block">
                  <SecondaryLink href={data.legalUrl} target="_blank" rel="noopener noreferrer">
                    Terms & Legal
                  </SecondaryLink>
                  &nbsp;
                  <SecondaryLink href={data.privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </SecondaryLink>
                  &nbsp;
                  <SecondaryLink href={data.trademarksUrl} target="_blank" rel="noopener noreferrer">
                    Trademarks
                  </SecondaryLink>
                </Box>
              </Text>
              <Box />
            </Flex.Item>
            <Flex.Item width={[null, 1, 2 / 4]} px={4}>
              <Text fontSize="1" color="bmtGrey50" lineHeight="20px" as="p">
                Brokerage services provided to clients of{' '}
                <SecondaryLink href="https://www.betterment.com/" target="_blank" rel="noopener noreferrer">
                  Betterment LLC
                </SecondaryLink>{' '}
                by{' '}
                <SecondaryLink href="https://www.bettermentsecurities.com/" target="_blank" rel="noopener noreferrer">
                  Betterment Securities
                </SecondaryLink>
                , an SEC registered broker-dealer and member{' '}
                <SecondaryLink href="https://www.finra.org/" rel="noopener noreferrer" target="_blank">
                  FINRA
                </SecondaryLink>
                /
                <SecondaryLink href="https://www.sipc.org/" rel="noopener noreferrer" target="_blank">
                  SIPC
                </SecondaryLink>
                .
              </Text>
              <Box mt="6">
                <Text fontSize="1" color="bmtGrey50" lineHeight="20px" as="p">
                  401(k) plan administration services provided by Betterment for Business LLC. Investment advice to
                  plans and plan participants provided by Betterment LLC, an SEC registered investment adviser.
                  Betterment LLC and Betterment Securities are affiliates of Betterment for Business LLC.
                </Text>
              </Box>
              <Box mt="6">
                <Text fontSize="1" color="bmtGrey50" lineHeight="20px" as="p">
                  Investments: Not FDIC Insured &bull; No Bank Guarantee &bull; May Lose Value. Investing in securities
                  involves risks, and there is always the potential of losing money when you invest in securities.
                  Before investing, consider your investment objectives and Betterment's charges and expenses. Past
                  performance does not guarantee future results, and the likelihood of investment outcomes are
                  hypothetical in nature. See full disclosures for more information. Not an offer, solicitation of an
                  offer, or advice to buy or sell securities in jurisdictions where Betterment is not registered.
                </Text>
              </Box>
            </Flex.Item>
            <Flex.Item width={[1, null, 1 / 4]} px="4">
              <Text fontSize="1" color="bmtGrey50" lineHeight="20px" as="p">
                &copy; Betterment Holdings Inc.
              </Text>
            </Flex.Item>
            <Flex.Item width={[1, null, 1 / 4]} px="4">
              <Text fontSize="1" color="bmtGrey50" lineHeight="20px" as="p">
                Any links provided to other websites are offered as a matter of convenience and are not intended to
                imply that Betterment or its authors endorse, sponsor, promote, and/or are affiliated with the owners of
                or participants in those sites, or endorses any information contained on those sites, unless expressly
                stated otherwise.
              </Text>
            </Flex.Item>
          </Flex>
        </SectionLayout.ContentWrapper>
      </SectionLayout>
    );
  }
  return null;
};

export default Footer;
